import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'
import {
  ImageConversionState,
  TFileWithState,
  UploaderState,
} from './UploadDropzone'
import {
  canFileBeConvertedToSvg,
  getFileNameFromFileUrl,
} from './UploadDropzone.utils'

interface IDropzoneData {
  filesWithState: TFileWithState[]
  setFilesWithState: Dispatch<SetStateAction<TFileWithState[]>>
  selectedFile: TFileWithState
  setSelectedFile: Dispatch<SetStateAction<TFileWithState>>
  hasSvgConvertableFile: boolean
}

const UploadDropzoneDataContext = createContext<IDropzoneData | undefined>(
  undefined
)

export function UploadDropzoneProvider({
  children,
  initialFileUrls,
  forceReset = false,
}: {
  children: ReactNode
  initialFileUrls: string[]
  forceReset?: boolean
}) {
  const [filesWithState, setFilesWithState] = useState<TFileWithState[]>([])
  const [selectedFile, setSelectedFile] = useState<TFileWithState>(null)
  const [hasSvgConvertableFile, setHasSvgConvertableFile] =
    useState<boolean>(false)

  useEffect(() => {
    if (initialFileUrls?.length > 0) {
      const initFilesWithState: TFileWithState[] = []
      for (const initialFileUrl of initialFileUrls) {
        const initialFile = {
          name: getFileNameFromFileUrl(initialFileUrl),
        } as unknown as File
        initFilesWithState.push({
          file: initialFile,
          state: UploaderState.SUCCESS,
          uploadingPercentage: 100,
          uploadedFileUrl: initialFileUrl,
          fileConversionState: ImageConversionState.NOT_CONVERTED,
        })
      }
      setFilesWithState(initFilesWithState)
    }
  }, [initialFileUrls])

  useEffect(() => {
    if (forceReset) {
      setFilesWithState([])
    }
  }, [forceReset])

  useEffect(() => {
    setHasSvgConvertableFile(
      filesWithState.some((fileWithState) =>
        canFileBeConvertedToSvg(fileWithState.file)
      )
    )
  }, [filesWithState])

  const providerValue = {
    filesWithState,
    setFilesWithState,
    selectedFile,
    setSelectedFile,
    hasSvgConvertableFile,
  }

  return (
    <UploadDropzoneDataContext.Provider value={providerValue}>
      {children}
    </UploadDropzoneDataContext.Provider>
  )
}

export function useFilesWithState() {
  const context = useContext(UploadDropzoneDataContext)
  if (context === undefined) {
    throw new Error(
      'useFilesWithState must be used within a UploadDropzoneProvider'
    )
  }
  return {
    filesWithState: context.filesWithState,
    setFilesWithState: (args) => {
      context.setFilesWithState(args)
    },
  }
}

export function useSelectedFile() {
  const context = useContext(UploadDropzoneDataContext)
  if (context === undefined) {
    throw new Error(
      'useSelectedFile must be used within a UploadDropzoneProvider'
    )
  }
  return {
    selectedFile: context.selectedFile || context.filesWithState[0],
    setSelectedFile: context.setSelectedFile,
  }
}

export function useHasSvgConvertableFiles() {
  const context = useContext(UploadDropzoneDataContext)
  if (context === undefined) {
    throw new Error(
      'useHasSvgConvertableFiles must be used within a UploadDropzoneProvider'
    )
  }
  return context.hasSvgConvertableFile
}
